import { useCallback, useEffect, useRef, useState } from "react";
import { useFormState } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Fechar um cadastro sem querer perdia tudo o que já tinha sido digitado: um
 * clique fora do diálogo, um Esc ou o X, e a jornada inteira sumia sem aviso.
 *
 * Com algo preenchido, sair agora pergunta antes. Com a ficha ainda em branco
 * não há o que perder, e o diálogo fecha direto como antes.
 *
 * Uso: `tentarSair` no `onOpenChange` do diálogo do cadastro, `aviso` ao lado
 * dele e `<RastrearAlteracoes>` dentro do formulário — é ele que sabe se algo
 * foi digitado, e o diálogo fica do lado de fora do formulário.
 */
export const useSairDoCadastro = (sair: () => void) => {
  const [perguntando, setPerguntando] = useState(false);
  const alterado = useRef(false);

  const tentarSair = () => {
    if (alterado.current) setPerguntando(true);
    else sair();
  };

  const aviso = (
    <Dialog open={perguntando} onOpenChange={setPerguntando}>
      <DialogContent className="z-[70] sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Sair sem salvar?</DialogTitle>
          <DialogDescription>Os dados preenchidos neste cadastro serão perdidos.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setPerguntando(false)}>
            Continuar preenchendo
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              setPerguntando(false);
              sair();
            }}
          >
            Sair sem salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const aoAlterar = useCallback((valor: boolean) => {
    alterado.current = valor;
  }, []);

  return { tentarSair, aviso, aoAlterar };
};

/** Avisa se o formulário em volta já tem algo digitado. */
export const RastrearAlteracoes = ({ aoMudar }: { aoMudar: (alterado: boolean) => void }) => {
  const { isDirty } = useFormState();
  useEffect(() => {
    aoMudar(isDirty);
  }, [isDirty, aoMudar]);
  // O formulário sai junto com o diálogo; reaberto, a ficha volta em branco.
  useEffect(() => () => aoMudar(false), [aoMudar]);
  return null;
};
